import type { Entity, PlayerForm, Vec2 } from '../components'
import type { GameWorld } from '../world'
import { T } from '../tunables'

type EyeSocketSpec = { offset: Vec2; radius: number }

// Divergent evolution — each critter form gets a deliberate eye read.
// Offsets/radii are in body-radius units (same convention as attachEyes).
// First-cut values; CDP-calibrated per form during the P2 art pass.
const AMOEBA_EYES: EyeSocketSpec[] = [
  { offset: { x: -0.38, y: 0.28 }, radius: 0.28 },
  { offset: { x:  0.38, y: 0.28 }, radius: 0.28 },
]
const FORM_EYES: Record<PlayerForm, EyeSocketSpec[]> = {
  // Cat: slightly asymmetric (6% size split) so the face has life.
  whiskerkit: [
    { offset: { x: -0.36, y: 0.30 }, radius: 0.26 },
    { offset: { x:  0.38, y: 0.30 }, radius: 0.29 },
  ],
  // Whale: small, wide-set, low — leaves the forehead clear for the tusk.
  narwhalen: [
    { offset: { x: -0.44, y: 0.18 }, radius: 0.21 },
    { offset: { x:  0.44, y: 0.18 }, radius: 0.21 },
  ],
  // Anemone: modest eyes riding high on the column, under the crown.
  anemonaut: [
    { offset: { x: -0.28, y: 0.38 }, radius: 0.22 },
    { offset: { x:  0.28, y: 0.38 }, radius: 0.22 },
  ],
  // Axolotl: big friendly wide-set saucers.
  frillgill: [
    { offset: { x: -0.40, y: 0.22 }, radius: 0.31 },
    { offset: { x:  0.40, y: 0.22 }, radius: 0.31 },
  ],
  // Anglerfish: big goofy headlights — but trimmed back from eclipsing the
  // body, so a sliver more fish reads around them.
  lanternet: [
    { offset: { x: -0.34, y: 0.25 }, radius: 0.35 },
    { offset: { x:  0.34, y: 0.25 }, radius: 0.35 },
  ],
}

// Pupil physics: spring-damper in the entity's accelerating frame.
// Under-damped so pupils bounce when the body jerks (follow-through).
// Tunables: T.eyes — see tunables.ts.

function randomBlinkInterval(): number {
  return T.eyes.blinkIntervalMin + Math.random() * (T.eyes.blinkIntervalMax - T.eyes.blinkIntervalMin)
}

export function attachEyes(
  entity: Entity,
  sockets: { offset: Vec2; radius: number }[],
  options: { angry?: number } = {},
): void {
  if (!entity.position) return
  entity.eyes = {
    sockets,
    pupilPos: sockets.map(() => ({ x: 0, y: 0 })),
    pupilVel: sockets.map(() => ({ x: 0, y: 0 })),
    prevVel: { x: 0, y: 0 },
    // Stagger initial blink timers so a flock of predators doesn't blink in unison.
    blinkTimer: randomBlinkInterval(),
    isBlinking: false,
    blinkAmount: 0,
    angry: options.angry ?? 0,
  }
}

/** (Re)attach the player's eyes for its lineage form — the amoeba default
 *  before speciation, the critter's sockets after. Safe to call any time the
 *  form changes (speciation, save restore, cheat.form). */
export function applyFormEyes(e: Entity): void {
  const form = e.lineage?.form
  const specs = form ? FORM_EYES[form] : AMOEBA_EYES
  // Clone so pupil state / future tweaks never mutate the shared tables.
  attachEyes(e, specs.map(s => ({ offset: { ...s.offset }, radius: s.radius })))
}

export function eyesSystem(world: GameWorld, dt: number): void {
  if (dt <= 0) return
  for (const e of world.with('eyes', 'position')) {
    const vel = e.velocity ?? { x: 0, y: 0 }
    const eyes = e.eyes
    const bodyR = e.renderable?.radius ?? 0.12

    // Acceleration (for inertial "jerk-reaction" pseudo-force).
    const ax = (vel.x - eyes.prevVel.x) / dt
    const ay = (vel.y - eyes.prevVel.y) / dt

    // Look-ahead target: pupil leans toward velocity direction, saturating at
    // LOOK_FRACTION of the socket's usable travel.
    const speed = Math.hypot(vel.x, vel.y)
    const lookScale = Math.min(speed / T.eyes.lookRefSpeed, 1) * T.eyes.lookFraction
    const dirX = speed > 0.001 ? vel.x / speed : 0
    const dirY = speed > 0.001 ? vel.y / speed : 0

    for (let s = 0; s < eyes.sockets.length; s++) {
      const socket = eyes.sockets[s]
      const p = eyes.pupilPos[s]
      const v = eyes.pupilVel[s]

      const socketR = socket.radius * bodyR
      const pupilR = socketR * 0.45
      const maxDisp = socketR - pupilR

      const targetX = dirX * maxDisp * lookScale
      const targetY = dirY * maxDisp * lookScale

      // Overlap principle: 6% stiffness asymmetry between left/right so the
      // two eyes don't track identically — subtle but adds life.
      const asym = 1 + (s === 0 ? -0.06 : 0.06)
      const k = T.eyes.springK * asym
      const c = T.eyes.damping / asym

      const fx = -ax - k * (p.x - targetX) - c * v.x
      const fy = -ay - T.eyes.gravity - k * (p.y - targetY) - c * v.y

      v.x += fx * dt
      v.y += fy * dt
      p.x += v.x * dt
      p.y += v.y * dt

      const len = Math.hypot(p.x, p.y)
      if (len > maxDisp && len > 0) {
        const nx = p.x / len
        const ny = p.y / len
        p.x = nx * maxDisp
        p.y = ny * maxDisp
        const vDotN = v.x * nx + v.y * ny
        if (vDotN > 0) {
          v.x -= (1 + T.eyes.restitution) * vDotN * nx
          v.y -= (1 + T.eyes.restitution) * vDotN * ny
        }
      }
    }

    // Hold-closed override (evolution gather): ease shut, stay shut, then
    // snap open fast on expiry — the wide-awake moment of arrival.
    if (eyes.holdClosedTimer !== undefined && eyes.holdClosedTimer > 0) {
      eyes.holdClosedTimer = Math.max(0, eyes.holdClosedTimer - dt)
      eyes.blinkAmount = Math.min(1, eyes.blinkAmount + dt * 8)
      eyes.isBlinking = false
      eyes.blinkTimer = randomBlinkInterval()
    } else {
      // Blink state machine (secondary action).
      eyes.blinkTimer -= dt
      if (!eyes.isBlinking && eyes.blinkTimer <= 0) {
        eyes.isBlinking = true
        eyes.blinkTimer = T.eyes.blinkDuration
      } else if (eyes.isBlinking && eyes.blinkTimer <= 0) {
        eyes.isBlinking = false
        eyes.blinkTimer = randomBlinkInterval()
      }
      if (eyes.isBlinking) {
        // Triangle wave peaking at mid-animation: 0 → 1 → 0.
        const phase = 1 - eyes.blinkTimer / T.eyes.blinkDuration
        eyes.blinkAmount = phase < 0.5 ? phase * 2 : (1 - phase) * 2
      } else {
        // Snap-open decay (fast, ~4 frames from fully shut) — only does work
        // after a hold-closed release; during normal life this is already 0.
        eyes.blinkAmount = Math.max(0, eyes.blinkAmount - dt * 14)
      }
    }

    // Ramp angry toward angryTarget (if set).
    if (eyes.angryTarget !== undefined) {
      const target = eyes.angryTarget
      const rate = target > eyes.angry ? T.eyes.angryRampRate : T.eyes.angryRelaxRate
      const delta = target - eyes.angry
      eyes.angry += Math.sign(delta) * Math.min(Math.abs(delta), rate * dt)
    }

    eyes.prevVel.x = vel.x
    eyes.prevVel.y = vel.y
  }
}

export function triggerEyeAlert(e: Entity): void {
  if (!e.eyes) return
  e.eyes.angryTarget = 1
}

/** Ease the eyes shut and hold them closed for `sec`; they snap open after. */
export function holdEyesClosed(e: Entity, sec: number): void {
  if (!e.eyes) return
  e.eyes.holdClosedTimer = sec
}

export function clearEyeAlert(e: Entity): void {
  if (!e.eyes) return
  e.eyes.angryTarget = 0
}
